import { z } from "zod"
import type { CreateProductRequest, Product, ProductVariant } from "./catalog"

/**
 * Импорт товаров из таблицы продавца (встреча 25.09.2026: «продавец в любой
 * форме выгрузил — за час максимум занесли»).
 *
 * Поток: кабинет читает файл (xlsx/csv) в строки → продавец сопоставляет
 * колонки с нашими полями → шлюз строит план (группирует строки-размеры в
 * товары, находит категории и уже заведённые товары по артикулу, помечает
 * ошибки по строкам) → продавец видит предпросмотр → шлюз создаёт/обновляет
 * товары порциями и докачивает фото по ссылкам.
 *
 * План строится здесь, чистой функцией, а не в шлюзе: тот же разбор показывает
 * мок-режим кабинета, и два места не разъедутся.
 */

// ── Поля ─────────────────────────────────────────────────────────────────

export const TABLE_IMPORT_FIELDS = [
  "name",
  "article",
  "barcode",
  "price",
  "oldPrice",
  "discountPercent",
  "stock",
  "size",
  "russianSize",
  "color",
  "category",
  "description",
  "photos",
  "brand",
  "composition",
] as const
export const TableImportFieldSchema = z.enum(TABLE_IMPORT_FIELDS)
export type TableImportField = z.infer<typeof TableImportFieldSchema>

/** Поля, которые могут прийти из нескольких колонок сразу (WB: «Фото 1», «Фото 2»…). */
export const MULTI_COLUMN_FIELDS: readonly TableImportField[] = ["photos"]

/** Нормализация заголовка: регистр, ё, пунктуация, лишние пробелы. */
export const normalizeHeader = (value: string): string =>
  value
    .toLowerCase()
    .replace(/ё/g, "е")
    .replace(/[^\p{L}\p{N}%]+/gu, " ")
    .replace(/\s+/g, " ")
    .trim()

/**
 * Синонимы заголовков. Сначала точные совпадения (после нормализации), потом —
 * вхождение ключевых слов. Список собран по выгрузкам WB, Ozon, Lamoda, 1С и
 * по тому, как подписывают колонки руками.
 */
const EXACT: Record<TableImportField, string[]> = {
  name: [
    "наименование", "название", "название товара", "наименование товара", "имя товара",
    "товар", "заголовок", "name", "title", "product name", "item name",
  ],
  article: [
    "артикул продавца", "артикул", "артикул поставщика", "артикул товара", "ваш sku",
    "sku", "vendor code", "article", "offer id", "артикул мп", "код товара", "модель",
  ],
  barcode: ["баркод", "баркоды", "штрихкод", "штрих код", "ean", "barcode", "gtin", "штрихкод ean"],
  price: [
    "цена", "цена руб", "цена со скидкой", "цена продажи", "текущая цена", "цена с учетом скидки",
    "розничная цена", "price", "цена р", "стоимость", "цена товара", "итоговая цена",
  ],
  oldPrice: [
    "цена до скидки", "старая цена", "цена без скидки", "зачеркнутая цена", "цена до скидки руб",
    "old price", "compare at price", "базовая цена", "цена до скидок",
  ],
  discountPercent: ["скидка", "скидка %", "скидка в %", "процент скидки", "discount", "discount %"],
  stock: [
    "остаток", "остатки", "количество", "кол во", "наличие", "остаток на складе", "доступно",
    "stock", "qty", "quantity", "остаток шт", "в наличии",
  ],
  size: ["размер", "размер производителя", "size", "размер товара", "размер на бирке"],
  russianSize: ["рос размер", "российский размер", "размер рф", "рос размер производителя", "ru size"],
  color: ["цвет", "цвет товара", "основной цвет", "color", "colour", "название цвета"],
  category: [
    "категория", "предмет", "тип", "тип товара", "раздел", "подкатегория", "категория товара",
    "category", "product type", "коммерческий тип",
  ],
  description: ["описание", "описание товара", "аннотация", "description"],
  photos: [
    "фото", "фотографии", "ссылки на фото", "ссылка на фото", "медиафайлы", "изображения",
    "картинки", "ссылка на изображение", "изображение", "главное фото", "фото товара",
    "ссылка на главное фото", "ссылки на дополнительные фото", "photo", "photos", "images", "image",
    "image url", "picture",
  ],
  brand: ["бренд", "торговая марка", "марка", "производитель", "brand", "vendor"],
  composition: ["состав", "материал", "состав материала", "материал изделия", "composition", "material"],
}

// Вхождения: если точного совпадения нет. Порядок важен — «цена до скидки»
// должна поймать «старую цену» раньше, чем «цена» поймает цену продажи.
const CONTAINS: [TableImportField, RegExp][] = [
  ["russianSize", /\bрос(сийский)?\b.*размер|размер.*\bрф\b/],
  ["oldPrice", /до скидки|без скидки|старая цена|зачеркнут/],
  ["discountPercent", /^скидка|процент скидки/],
  ["price", /цена|стоимость|price/],
  ["barcode", /баркод|штрих|ean|barcode/],
  ["article", /артикул|sku|vendor code/],
  ["photos", /фото|изображени|картинк|image|photo|медиа/],
  ["stock", /остат|количеств|наличи|stock|qty/],
  ["size", /размер|size/],
  ["color", /цвет|colou?r/],
  ["category", /категори|предмет|category/],
  ["description", /описани|description/],
  ["composition", /состав|материал/],
  ["brand", /бренд|brand|торгов.*марк/],
  ["name", /наименовани|название|name|title/],
]

const exactField = (normalized: string): TableImportField | undefined => {
  for (const field of TABLE_IMPORT_FIELDS) {
    if (EXACT[field].includes(normalized)) return field
  }
  return undefined
}

export const guessField = (header: string): TableImportField | undefined => {
  const normalized = normalizeHeader(header)
  if (!normalized) return undefined
  return exactField(normalized) ?? CONTAINS.find(([, pattern]) => pattern.test(normalized))?.[0]
}

/** Колонка → поле; null — «не импортировать». */
export type TableImportMapping = (TableImportField | null)[]

/**
 * Автоподсказка сопоставления. Каждое поле берёт одну колонку (кроме фото).
 * Особый случай WB: там «Цена» — это цена до скидки, а продажная — «Цена со
 * скидкой». Если в файле есть обе, простая «Цена» становится старой ценой.
 */
export const suggestMapping = (headers: string[]): TableImportMapping => {
  const guesses = headers.map((header) => ({
    field: guessField(header),
    exact: exactField(normalizeHeader(header)) !== undefined,
  }))
  const normalized = headers.map(normalizeHeader)
  const hasDiscounted = normalized.some((h) => /со скидкой|с учетом скидки/.test(h))
  const hasOldPrice = guesses.some((g) => g.field === "oldPrice")
  if (hasDiscounted && !hasOldPrice) {
    normalized.forEach((h, index) => {
      if (h === "цена" || h === "цена руб") guesses[index] = { field: "oldPrice", exact: true }
    })
  }
  const mapping: TableImportMapping = headers.map(() => null)
  const taken = new Set<TableImportField>()
  // Точные совпадения выбирают колонку первыми, вхождения — из оставшихся.
  for (const pass of [true, false]) {
    guesses.forEach((guess, index) => {
      if (!guess.field || guess.exact !== pass || mapping[index]) return
      if (taken.has(guess.field) && !MULTI_COLUMN_FIELDS.includes(guess.field)) return
      mapping[index] = guess.field
      taken.add(guess.field)
    })
  }
  return mapping
}

/**
 * Строка заголовков: в выгрузках маркетплейсов над ней бывает строка-шапка
 * («Шаблон для загрузки…») или описание колонок. Берём среди первых десяти
 * ту, где больше всего узнаваемых заголовков.
 */
export const detectHeaderRow = (rows: { n: number; cells: string[] }[]): number => {
  let best = 0
  let bestScore = 0
  rows.slice(0, 10).forEach((row, index) => {
    const score = row.cells.filter((cell) => guessField(cell)).length
    if (score > bestScore) {
      best = index
      bestScore = score
    }
  })
  return best
}

// ── Шаблоны ──────────────────────────────────────────────────────────────

export const TableImportGroupBySchema = z.enum(["article", "name", "row"])
export type TableImportGroupBy = z.infer<typeof TableImportGroupBySchema>

export const TableImportOptionsSchema = z.object({
  /** Категория для строк, где своя не указана или не найдена. */
  defaultCategoryId: z.string().optional(),
  /** Бренд для строк без бренда. */
  defaultBrand: z.string().max(120).optional(),
  /** Как собирать строки-размеры в один товар. */
  groupBy: TableImportGroupBySchema.optional(),
})
export type TableImportOptions = z.infer<typeof TableImportOptionsSchema>

export const ImportTemplateSchema = z.object({
  id: z.string(),
  name: z.string(),
  /** Нормализованный заголовок файла → поле. */
  mapping: z.record(TableImportFieldSchema),
  options: TableImportOptionsSchema,
  createdAt: z.string(),
  updatedAt: z.string(),
})
export type ImportTemplate = z.infer<typeof ImportTemplateSchema>

export const ImportTemplateInputSchema = z.object({
  name: z.string().trim().min(1).max(80),
  mapping: z.record(TableImportFieldSchema),
  options: TableImportOptionsSchema.optional(),
})
export type ImportTemplateInput = z.infer<typeof ImportTemplateInputSchema>

/** Сопоставление экрана → то, что сохраняем в шаблон (по заголовкам, не по номерам колонок). */
export const mappingToTemplate = (
  headers: string[],
  mapping: TableImportMapping,
): Record<string, TableImportField> => {
  const out: Record<string, TableImportField> = {}
  headers.forEach((header, index) => {
    const field = mapping[index]
    const key = normalizeHeader(header)
    if (field && key) out[key] = field
  })
  return out
}

/** Доля заголовков шаблона, найденных в файле: 1 — файл той же формы. */
export const templateMatch = (headers: string[], template: Record<string, TableImportField>): number => {
  const keys = Object.keys(template)
  if (!keys.length) return 0
  const present = new Set(headers.map(normalizeHeader))
  return keys.filter((key) => present.has(key)).length / keys.length
}

export const applyTemplate = (
  headers: string[],
  template: Record<string, TableImportField>,
): TableImportMapping => headers.map((header) => template[normalizeHeader(header)] ?? null)

// ── Запрос и план ────────────────────────────────────────────────────────

export const TABLE_IMPORT_MAX_ROWS = 5000

export const TableImportRowSchema = z.object({
  /** Номер строки в файле — для сообщений продавцу. */
  n: z.number().int().positive(),
  cells: z.array(z.string().max(20000)).max(300),
})
export type TableImportRow = z.infer<typeof TableImportRowSchema>

export const TableImportRequestSchema = z.object({
  headers: z.array(z.string().max(500)).max(300),
  rows: z.array(TableImportRowSchema).min(1).max(TABLE_IMPORT_MAX_ROWS),
  mapping: z.array(TableImportFieldSchema.nullable()).max(300),
  options: TableImportOptionsSchema.default({}),
})
export type TableImportRequest = z.infer<typeof TableImportRequestSchema>

export type TableImportIssue = {
  /** Номер строки файла; 0 — замечание ко всему файлу. */
  row: number
  field?: TableImportField
  level: "error" | "warning"
  message: string
}

export type TableImportVariant = {
  row: number
  size: string
  russianSize?: string
  color: string
  price: number
  oldPrice?: number
  stock: number
  barcode?: string
}

export type TableImportGroup = {
  key: string
  rows: number[]
  action: "create" | "update"
  /** Уже заведённый товар магазина с тем же артикулом. */
  productId?: string
  name: string
  article: string
  brand: string
  categoryId?: string
  categoryName?: string
  /** Категория пришла из колонки файла, а не из «категории по умолчанию». */
  categoryFromFile: boolean
  color: string
  description: string
  composition?: string
  photos: string[]
  variants: TableImportVariant[]
  issues: TableImportIssue[]
  valid: boolean
}

export type TableImportPreview = {
  groups: TableImportGroup[]
  /** Замечания ко всему файлу (например, не сопоставлена цена). */
  issues: TableImportIssue[]
  summary: {
    rows: number
    products: number
    create: number
    update: number
    invalid: number
    /** Строки, которые не попадут в импорт из-за ошибок. */
    errorRows: number
  }
  /** Поля, сопоставленные хотя бы с одной колонкой. */
  fields: TableImportField[]
}

export type TableImportCommitItem = {
  key: string
  rows: number[]
  name: string
  article: string
  status: "created" | "updated" | "failed"
  productId?: string
  issues: TableImportIssue[]
}

export type TableImportCommitResult = {
  created: number
  updated: number
  failed: number
  items: TableImportCommitItem[]
}

export type ImportCategory = {
  id: string
  name: string
  parentId: string | null
  /** Пол узла с наследованием (female / male / kids / unisex) — различает «Обувь» трёх аудиторий. */
  gender?: string | null
}
export type ImportExistingProduct = { id: string; article: string }

export type TableImportContext = {
  categories: ImportCategory[]
  existing: ImportExistingProduct[]
}

/** «1 499,00 ₽» → 1499; пусто → null; мусор → NaN. */
export const parseMoney = (raw: string | undefined): number | null => {
  let value = (raw ?? "").replace(/[\s  ]/g, "").replace(/(руб\.?|р\.|₽|rub|rur)/gi, "")
  if (!value) return null
  if (value.includes(",") && value.includes(".")) {
    // Разделитель тысяч — тот, что стоит раньше.
    value = value.indexOf(",") < value.indexOf(".") ? value.replace(/,/g, "") : value.replace(/\./g, "").replace(",", ".")
  } else if (value.includes(",")) {
    value = /,\d{3}$/.test(value) && !/,\d{1,2}$/.test(value) ? value.replace(/,/g, "") : value.replace(",", ".")
  }
  if (!/^-?\d+(\.\d+)?$/.test(value)) return Number.NaN
  return Math.round(Number(value) * 100) / 100
}

const parseCount = (raw: string | undefined): number | null => {
  const value = (raw ?? "").replace(/[\s ]/g, "").replace(/шт\.?/i, "")
  if (!value) return null
  const n = Number(value.replace(",", "."))
  return Number.isFinite(n) ? n : Number.NaN
}

export const splitPhotoLinks = (raw: string | undefined): { links: string[]; invalid: string[] } => {
  const links: string[] = []
  const invalid: string[] = []
  for (const part of (raw ?? "").split(/[\s;|]+|,(?=\s*https?:)/i)) {
    const value = part.trim()
    if (!value) continue
    if (/^https?:\/\/[^\s]+$/i.test(value)) links.push(value)
    else invalid.push(value)
  }
  return { links, invalid }
}

const norm = (value: string) => value.toLowerCase().replace(/ё/g, "е").replace(/\s+/g, " ").trim()

export type ImportAudience = "female" | "male" | "kids"

/** Аудитория по тексту (название товара, имя корня): дети → женщины → мужчины. */
const AUDIENCE_HINTS: [ImportAudience, RegExp][] = [
  ["kids", /(^|[^a-zа-яё])(boys|girls|kids|kid|baby|infants?|toddler)([^a-zа-яё]|$)|детск|детям|для мальчик|для девоч|девочк|мальчик|малыш/i],
  ["female", /(^|[^a-zа-яё])(women|woman|womens|ladies)([^a-zа-яё]|$)|женск|женщин/i],
  ["male", /(^|[^a-zа-яё])(men|man|mens)([^a-zа-яё]|$)|мужск|мужчин/i],
]

export const importAudienceOf = (text: string | undefined): ImportAudience | undefined =>
  text ? AUDIENCE_HINTS.find(([, pattern]) => pattern.test(text))?.[0] : undefined

export type CategoryMatch =
  | { status: "found"; category: ImportCategory }
  /** Одно имя у нескольких узлов («Обувь» у Женщинам, Мужчинам и Детям), и ни путь, ни аудитория не решают. */
  | { status: "ambiguous"; candidates: ImportCategory[] }
  | { status: "missing" }

/**
 * Категория по названию из файла: «Платья», «Женщинам / Платья», «Одежда > Платья».
 *
 * Одно имя у нескольких узлов — обычное дело в дереве v2 («Обувь» у трёх
 * аудиторий). Раньше брался первый ЛИСТ — и старая «Обувь» молча уходила в
 * детскую (у детей «Обувь» — лист). Теперь: путь из файла → аудитория из
 * подсказки (название товара: «кеды женские») → если выбор всё ещё не один —
 * «неоднозначно», продавец уточняет путь.
 */
export const resolveCategoryMatch = (
  value: string,
  categories: ImportCategory[],
  hint?: { text?: string },
): CategoryMatch => {
  const parts = value.split(/\s*(?:\/|>|→|\\|\|)\s*/).map(norm).filter(Boolean)
  if (!parts.length) return { status: "missing" }
  const byId = new Map(categories.map((c) => [c.id, c]))
  const last = parts[parts.length - 1] as string
  const candidates = categories.filter((c) => norm(c.name) === last)
  const withPath = candidates.filter((c) => {
    let parent = c.parentId ? byId.get(c.parentId) : undefined
    for (let i = parts.length - 2; i >= 0; i -= 1) {
      while (parent && norm(parent.name) !== parts[i]) parent = parent.parentId ? byId.get(parent.parentId) : undefined
      if (!parent) return false
      parent = parent.parentId ? byId.get(parent.parentId) : undefined
    }
    return true
  })
  const pool = withPath.length ? withPath : candidates
  if (pool.length === 0) return { status: "missing" }
  if (pool.length === 1) return { status: "found", category: pool[0] as ImportCategory }

  const audience = importAudienceOf(hint?.text)
  if (audience) {
    const audienceOfNode = (node: ImportCategory): ImportAudience | undefined => {
      if (node.gender === "female" || node.gender === "male" || node.gender === "kids") return node.gender
      let root = node
      for (let guard = 0; root.parentId && guard < 10; guard += 1) root = byId.get(root.parentId) ?? root
      return importAudienceOf(root.name)
    }
    const same = pool.filter((c) => audienceOfNode(c) === audience)
    if (same.length === 1) return { status: "found", category: same[0] as ImportCategory }
  }
  return { status: "ambiguous", candidates: pool }
}

/** Найденная категория или `undefined` (нет такой или неоднозначно). */
export const matchCategory = (
  value: string,
  categories: ImportCategory[],
  hint?: { text?: string },
): ImportCategory | undefined => {
  const match = resolveCategoryMatch(value, categories, hint)
  return match.status === "found" ? match.category : undefined
}

/** Путь узла для сообщения: «Женщинам / Обувь». */
const categoryPathLabel = (node: ImportCategory, categories: ImportCategory[]): string => {
  const byId = new Map(categories.map((c) => [c.id, c]))
  const names: string[] = []
  for (let cur: ImportCategory | undefined = node; cur && names.length < 10; cur = cur.parentId ? byId.get(cur.parentId) : undefined) {
    names.unshift(cur.name)
  }
  return names.join(" / ")
}

type ParsedRow = {
  n: number
  values: Partial<Record<TableImportField, string>>
  photos: string[]
}

const readRow = (row: TableImportRow, mapping: TableImportMapping): ParsedRow => {
  const values: Partial<Record<TableImportField, string>> = {}
  const photos: string[] = []
  mapping.forEach((field, index) => {
    if (!field) return
    const cell = (row.cells[index] ?? "").trim()
    if (!cell) return
    if (field === "photos") photos.push(cell)
    else if (values[field] === undefined) values[field] = cell
  })
  return { n: row.n, values, photos }
}

const fieldLabel: Record<TableImportField, string> = {
  name: "Название",
  article: "Артикул",
  barcode: "Баркод",
  price: "Цена",
  oldPrice: "Цена до скидки",
  discountPercent: "Скидка, %",
  stock: "Остаток",
  size: "Размер",
  russianSize: "Рос. размер",
  color: "Цвет",
  category: "Категория",
  description: "Описание",
  photos: "Фото",
  brand: "Бренд",
  composition: "Состав",
}
export const TABLE_IMPORT_FIELD_LABELS_RU = fieldLabel

const fmt = (n: number) => String(n).replace(/\B(?=(\d{3})+(?!\d))/g, " ")

/** План импорта: группы-товары с вариантами и ошибками по строкам. */
export const buildImportPlan = (
  request: TableImportRequest,
  context: TableImportContext,
): TableImportPreview => {
  const { mapping, options } = request
  const fields = [...new Set(mapping.filter((f): f is TableImportField => Boolean(f)))]
  const has = (field: TableImportField) => fields.includes(field)
  const fileIssues: TableImportIssue[] = []
  if (!has("price") && !has("oldPrice")) {
    fileIssues.push({ row: 0, field: "price", level: "error", message: "Не выбрана колонка с ценой" })
  }
  if (!has("name") && !has("article")) {
    fileIssues.push({
      row: 0,
      field: "name",
      level: "error",
      message: "Не выбрана ни колонка с названием, ни колонка с артикулом",
    })
  }
  const groupBy: TableImportGroupBy =
    options.groupBy ?? (has("article") ? "article" : "name")
  const defaultCategory = options.defaultCategoryId
    ? context.categories.find((c) => c.id === options.defaultCategoryId)
    : undefined
  const existingByArticle = new Map<string, ImportExistingProduct>()
  for (const product of context.existing) {
    const key = norm(product.article)
    if (key && !existingByArticle.has(key)) existingByArticle.set(key, product)
  }

  const groups = new Map<string, { rows: ParsedRow[] }>()
  const order: string[] = []
  const orphanIssues: TableImportIssue[] = []
  for (const row of request.rows) {
    const parsed = readRow(row, mapping)
    const article = parsed.values.article ?? ""
    const name = parsed.values.name ?? ""
    const color = parsed.values.color ?? ""
    let key: string
    if (groupBy === "row") key = `r:${row.n}`
    else if (groupBy === "article" && article) key = `a:${norm(article)}|${norm(color)}`
    else if (name) key = `n:${norm(name)}|${norm(color)}`
    else if (article) key = `a:${norm(article)}|${norm(color)}`
    else {
      if (Object.keys(parsed.values).length || parsed.photos.length) {
        orphanIssues.push({
          row: row.n,
          level: "error",
          message: "В строке нет ни названия, ни артикула — не понять, к какому товару она относится",
        })
      }
      continue
    }
    if (!groups.has(key)) {
      groups.set(key, { rows: [] })
      order.push(key)
    }
    groups.get(key)?.rows.push(parsed)
  }

  const result: TableImportGroup[] = []
  for (const key of order) {
    const rows = groups.get(key)?.rows ?? []
    const first = <F extends TableImportField>(field: F) =>
      rows.find((row) => row.values[field])?.values[field] ?? ""
    const issues: TableImportIssue[] = []
    const firstRow = rows[0]?.n ?? 0
    const article = first("article")
    const existing = article ? existingByArticle.get(norm(article)) : undefined
    const name = first("name")
    const action: TableImportGroup["action"] = existing ? "update" : "create"

    if (action === "create" && !name) {
      issues.push({ row: firstRow, field: "name", level: "error", message: "Не указано название товара" })
    }
    if (name.length > 200) {
      issues.push({ row: firstRow, field: "name", level: "error", message: "Название длиннее 200 символов" })
    }

    // Категория.
    const categoryRaw = first("category")
    let category: ImportCategory | undefined
    let categoryFromFile = false
    if (categoryRaw) {
      const match = resolveCategoryMatch(categoryRaw, context.categories, { text: name })
      category = match.status === "found" ? match.category : undefined
      categoryFromFile = Boolean(category)
      if (match.status === "ambiguous") {
        const variants = match.candidates.map((c) => `«${categoryPathLabel(c, context.categories)}»`).join(", ")
        issues.push({
          row: firstRow,
          field: "category",
          level: action === "create" && !defaultCategory ? "error" : "warning",
          message: `Категория «${categoryRaw}» есть в нескольких разделах (${variants}) — укажите путь, например ${match.candidates[0] ? `«${categoryPathLabel(match.candidates[0], context.categories)}»` : ""}${
            defaultCategory && action === "create" ? `; пока товар попадёт в «${defaultCategory.name}»` : ""
          }`,
        })
        if (defaultCategory && action === "create") category = defaultCategory
      } else if (!category) {
        if (defaultCategory && action === "create") {
          category = defaultCategory
          issues.push({
            row: firstRow,
            field: "category",
            level: "warning",
            message: `Категория «${categoryRaw}» не найдена — товар попадёт в «${defaultCategory.name}»`,
          })
        } else if (action === "create") {
          issues.push({
            row: firstRow,
            field: "category",
            level: "error",
            message: `Категория «${categoryRaw}» не найдена на площадке — выберите категорию по умолчанию`,
          })
        } else {
          issues.push({
            row: firstRow,
            field: "category",
            level: "warning",
            message: `Категория «${categoryRaw}» не найдена — у товара останется прежняя`,
          })
        }
      }
    } else if (action === "create") {
      if (defaultCategory) category = defaultCategory
      else {
        issues.push({
          row: firstRow,
          field: "category",
          level: "error",
          message: "Не указана категория — выберите категорию по умолчанию",
        })
      }
    }

    let description = first("description")
    if (description.length > 2000) {
      description = description.slice(0, 2000)
      issues.push({
        row: firstRow,
        field: "description",
        level: "warning",
        message: "Описание длиннее 2000 символов — обрежем",
      })
    }

    // Фото — со всех строк группы, без повторов.
    const photos: string[] = []
    for (const row of rows) {
      for (const cell of row.photos) {
        const { links, invalid } = splitPhotoLinks(cell)
        for (const link of links) if (!photos.includes(link)) photos.push(link)
        for (const bad of invalid.slice(0, 1)) {
          issues.push({
            row: row.n,
            field: "photos",
            level: "warning",
            message: `«${bad.slice(0, 60)}» не похоже на ссылку на фото — пропустим`,
          })
        }
      }
    }
    if (photos.length > 15) {
      issues.push({
        row: firstRow,
        field: "photos",
        level: "warning",
        message: `Фото ${photos.length} — загрузим первые 15`,
      })
      photos.length = 15
    }

    // Варианты: каждая строка — размер.
    const variants: TableImportVariant[] = []
    const rowLevel: TableImportIssue[] = []
    const seenSizes = new Map<string, number>()
    for (const row of rows) {
      const v = row.values
      const rowIssues: TableImportIssue[] = []
      let price = parseMoney(v.price)
      let oldPrice = parseMoney(v.oldPrice)
      const discount = parseCount(v.discountPercent?.replace("%", ""))
      if (price !== null && Number.isNaN(price)) {
        rowIssues.push({ row: row.n, field: "price", level: "error", message: `Цена «${v.price}» не похожа на число` })
      }
      if (oldPrice !== null && Number.isNaN(oldPrice)) {
        rowIssues.push({
          row: row.n,
          field: "oldPrice",
          level: "warning",
          message: `Цена до скидки «${v.oldPrice}» не похожа на число — пропустим`,
        })
        oldPrice = null
      }
      if (price === null && oldPrice !== null && !Number.isNaN(oldPrice)) {
        if (discount !== null && Number.isFinite(discount) && discount > 0 && discount < 100) {
          price = Math.round(oldPrice * (100 - discount)) / 100
          price = Math.round(price)
        } else {
          price = oldPrice
          oldPrice = null
        }
      }
      if (price === null) {
        rowIssues.push({ row: row.n, field: "price", level: "error", message: "Не указана цена" })
      } else if (!Number.isNaN(price) && price <= 0) {
        rowIssues.push({ row: row.n, field: "price", level: "error", message: "Цена должна быть больше нуля" })
      }
      if (price !== null && oldPrice !== null && !Number.isNaN(price) && oldPrice <= price) {
        if (oldPrice < price) {
          rowIssues.push({
            row: row.n,
            field: "oldPrice",
            level: "warning",
            message: `Цена до скидки ${fmt(oldPrice)} ₽ меньше цены продажи — скидку не ставим`,
          })
        }
        oldPrice = null
      }
      const stockRaw = parseCount(v.stock)
      let stock = 0
      if (stockRaw !== null) {
        if (Number.isNaN(stockRaw) || stockRaw < 0) {
          rowIssues.push({ row: row.n, field: "stock", level: "error", message: `Остаток «${v.stock}» — не число` })
        } else stock = Math.floor(stockRaw)
      }
      const size = (v.size ?? "").trim() || (v.russianSize ?? "").trim()
      const sizeKey = `${norm(size || "универсальный")}|${norm(v.color ?? "")}`
      const duplicateOf = seenSizes.get(sizeKey)
      if (duplicateOf !== undefined && groupBy !== "row") {
        rowIssues.push({
          row: row.n,
          field: "size",
          level: "error",
          message: size
            ? `Размер «${size}» уже есть в строке ${duplicateOf} этого товара`
            : `Строка повторяет строку ${duplicateOf}: у товара без размеров может быть только одна строка`,
        })
      }
      issues.push(...rowIssues)
      rowLevel.push(...rowIssues)
      if (rowIssues.some((issue) => issue.level === "error")) continue
      seenSizes.set(sizeKey, row.n)
      variants.push({
        row: row.n,
        size,
        russianSize: v.russianSize && v.russianSize !== size ? v.russianSize : undefined,
        color: v.color ?? first("color"),
        price: price as number,
        oldPrice: oldPrice ?? undefined,
        stock,
        barcode: v.barcode,
      })
    }
    if (!variants.length && rows.length > 1) {
      issues.push({ row: firstRow, level: "error", message: "У товара не осталось ни одной строки без ошибок" })
    }
    if (variants.length > 50) {
      issues.push({ row: firstRow, field: "size", level: "error", message: "Больше 50 размеров у одного товара" })
    }

    // Ошибки строк-размеров убирают только сами строки; ошибки товара
    // (название, категория) и файла не дают завести товар целиком.
    const variantRowIssues = new Set(rowLevel)
    const groupErrors = issues.some((issue) => issue.level === "error" && !variantRowIssues.has(issue))
    const valid = variants.length > 0 && !groupErrors && !fileIssues.length

    result.push({
      key,
      rows: rows.map((row) => row.n),
      action,
      productId: existing?.id,
      name,
      article,
      brand: first("brand") || options.defaultBrand || "",
      categoryId: category?.id,
      categoryName: category?.name,
      categoryFromFile,
      color: first("color"),
      description,
      composition: first("composition") || undefined,
      photos,
      variants,
      issues,
      valid,
    })
  }

  // Строки без товара показываем отдельной «группой» ошибок в сводке файла.
  const errorRows = new Set<number>()
  for (const group of result) {
    const variantRows = new Set(group.variants.map((v) => v.row))
    for (const n of group.rows) if (!group.valid || !variantRows.has(n)) errorRows.add(n)
  }
  for (const issue of orphanIssues) errorRows.add(issue.row)

  return {
    groups: result,
    issues: [...fileIssues, ...orphanIssues],
    fields,
    summary: {
      rows: request.rows.length,
      products: result.length,
      create: result.filter((g) => g.valid && g.action === "create").length,
      update: result.filter((g) => g.valid && g.action === "update").length,
      invalid: result.filter((g) => !g.valid).length,
      errorRows: errorRows.size,
    },
  }
}

// ── Группа → запрос каталога ─────────────────────────────────────────────

const variantKey = (size: string, color: string) =>
  `${norm(size || "универсальный")}|${norm(color)}`

const importedVariant = (variant: TableImportVariant, id: string, position: number): ProductVariant => ({
  id,
  color: variant.color,
  size: variant.size,
  options: {
    ...(variant.russianSize ? { russianSize: variant.russianSize } : {}),
    ...(variant.barcode ? { barcode: variant.barcode } : {}),
  },
  price: variant.price,
  oldPrice: variant.oldPrice,
  stock: variant.stock,
  photos: [],
  position,
})

/** Новый товар: всё, чего нет в файле, — значения по умолчанию, как у пустой формы. */
export const groupToCreateRequest = (
  group: TableImportGroup,
  photos: string[],
  newVariantId: () => string,
): CreateProductRequest => ({
  name: group.name,
  brand: group.brand,
  categoryId: group.categoryId ?? "",
  sellerArticle: group.article,
  color: group.color,
  description: group.description,
  customAttributes: group.composition ? [{ label: "Состав", value: group.composition.slice(0, 500) }] : [],
  keywords: [],
  keyPhrases: [],
  photos,
  noWarranty: true,
  madeToOrder: false,
  characteristics: {},
  customization: { enabled: false, options: [] },
  delivery: { length: 0, width: 0, height: 0, weight: 0, ownDelivery: false, expressDelivery: false, pickup: true },
  variants: group.variants.map((variant, index) => importedVariant(variant, newVariantId(), index)),
})

/**
 * Правка заведённого товара: меняем только то, что есть в файле. Размеры
 * совпадают по названию и цвету — у них сохраняется id (и промодерированная
 * цена), новые добавляются, не упомянутые в файле остаются как были.
 */
export const groupToUpdateRequest = (
  group: TableImportGroup,
  existing: Product,
  fields: TableImportField[],
  photos: string[],
  newVariantId: () => string,
): Partial<CreateProductRequest> => {
  const has = (field: TableImportField) => fields.includes(field)
  const patch: Partial<CreateProductRequest> = {}
  if (has("name") && group.name) patch.name = group.name
  if (has("brand") && group.brand) patch.brand = group.brand
  if (has("color") && group.color) patch.color = group.color
  if (has("description") && group.description) patch.description = group.description
  if (has("category") && group.categoryFromFile && group.categoryId) patch.categoryId = group.categoryId
  if (photos.length) patch.photos = photos
  if (has("composition") && group.composition) {
    const others = (existing.customAttributes ?? []).filter((attr) => norm(attr.label) !== "состав")
    patch.customAttributes = [{ label: "Состав", value: group.composition.slice(0, 500) }, ...others].slice(0, 10)
  }
  const current = existing.variants ?? []
  const byKey = new Map(current.map((variant) => [variantKey(variant.size, variant.color), variant]))
  const touched = new Set<string>()
  const merged: ProductVariant[] = current.map((variant) => ({ ...variant }))
  for (const imported of group.variants) {
    const key = variantKey(imported.size, imported.color)
    const match =
      byKey.get(key) ??
      // Товар без размеров в файле и один размер в карточке — это он и есть.
      (!imported.size && current.length === 1 ? current[0] : undefined)
    if (match && !touched.has(match.id)) {
      touched.add(match.id)
      const target = merged.find((variant) => variant.id === match.id)
      if (!target) continue
      if (has("price") || has("oldPrice") || has("discountPercent")) {
        target.price = imported.price
        target.oldPrice = imported.oldPrice
      }
      if (has("stock")) target.stock = imported.stock
      if (imported.russianSize || imported.barcode) {
        target.options = {
          ...(target.options ?? {}),
          ...(imported.russianSize ? { russianSize: imported.russianSize } : {}),
          ...(imported.barcode ? { barcode: imported.barcode } : {}),
        }
      }
    } else {
      merged.push(importedVariant(imported, newVariantId(), merged.length))
    }
  }
  patch.variants = merged
  return patch
}
